/**
 * "Ahead membership" details popup — the membership counterpart to
 * `ProductUspsModal` (the Advanced blood-test popup). Same visual shell
 * (desktop overlay + mobile IOSBottomSheet, header / sections / sticky CTA)
 * but with static membership content instead of a Supabase USP fetch, since
 * membership isn't a bookable product in this flow.
 */
import { useEffect, useRef } from "react";
import { X, Check } from "lucide-react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { useTranslation } from "react-i18next";
import { PersonHeartIcon } from "@/components/icons/PersonHeartIcon";
import { CareIcon } from "@/components/icons/CareIcon";
import { BookingLanguage } from "@/types/booking";
import { languageMap, BOOKING_NS } from "@/i18n";
import { Product } from "../types";
import { MembershipTier } from "@/hooks/createProductsQueryFn";
import { MembershipBenefitList } from "../MembershipBenefits";
import { ProductInfoSection } from "./ProductInfoSection";
import { IOSBottomSheet } from "./IOSBottomSheet";
import { useBreakpoint } from "../hooks/useBreakpoint";

/* ------------------------------------------------------------------ thumb --- */

/** 92×92 rounded thumbnail — the dark Ahead pill (same asset as the card). */
const MembershipThumb = () => (
  <div
    className="flex h-[92px] w-[92px] flex-shrink-0 items-center justify-center overflow-hidden rounded-2xl"
    style={{
      backgroundImage: "url(/membership-logo-bg.webp)",
      backgroundSize: "cover",
      backgroundPosition: "center",
    }}
  >
    <svg width="48" height="51" viewBox="0 0 15 16" fill="none" aria-hidden>
      <path
        d="M11.3742 0H0V1.99316L11.2938 1.71601C12.3158 1.69133 13.1522 2.51707 13.1311 3.53073L12.8861 15.7934H14.8593V3.4586C14.8593 1.54897 13.2976 0 11.3723 0Z"
        fill="white"
      />
      <path
        d="M9.37226 4.30513L0 14.3525L1.42584 15.8028L10.8517 5.66047C11.2325 5.25045 11.2019 4.61264 10.7828 4.24059C10.3732 3.87612 9.74546 3.9046 9.37226 4.30513Z"
        fill="white"
      />
    </svg>
  </div>
);

/* ------------------------------------------------------------------ modal --- */

interface MembershipDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  language: BookingLanguage;
  /** Advanced blood-test product — its USP "Information" block is reused here. */
  infoProduct?: Product | null;
  onStart?: () => void;
  membershipTiers?: MembershipTier[];
}

const SectionHeading = ({ children }: { children: React.ReactNode }) => (
  <div className="px-4 py-2">
    <p className="text-base font-medium text-black">{children}</p>
  </div>
);

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

const TIER_DEFAULTS = [
  { id: "once", price: "CHF 299" },
  { id: "twice", price: "CHF 499" },
  { id: "four", price: "CHF 899" },
];

export const MembershipDetailsModal = ({
  isOpen,
  onClose,
  language,
  infoProduct,
  onStart,
  membershipTiers,
}: MembershipDetailsModalProps) => {
  const isMobile = useBreakpoint(640);
  const containerRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation(BOOKING_NS, { lng: languageMap[language] });

  const tiers =
    membershipTiers && membershipTiers.length > 0
      ? membershipTiers.map((tier) => ({
          name: t(`bloodTracking.membership.frequency${capitalize(tier.id)}`),
          tests: t(`bloodTracking.membership.contents${capitalize(tier.id)}`),
          price: tier.annual_amount_chf
            ? `CHF ${Math.round(tier.annual_amount_chf / 100)}`
            : (TIER_DEFAULTS.find((d) => d.id === tier.id)?.price ?? ""),
        }))
      : TIER_DEFAULTS.map((d) => ({
          name: t(`bloodTracking.membership.frequency${capitalize(d.id)}`),
          tests: t(`bloodTracking.membership.contents${capitalize(d.id)}`),
          price: d.price,
        }));

  const cheapestAnnual = membershipTiers?.[0]?.annual_amount_chf
    ? Math.round(membershipTiers[0].annual_amount_chf / 100)
    : null;

  // Lock body scroll while open (the overlay scrolls instead).
  useEffect(() => {
    if (!isOpen || isMobile) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen, isMobile]);

  const content = (
    <div className="flex flex-col gap-4 px-4 sm:gap-6 sm:px-6">
      {/* Header */}
      <div className="flex items-start gap-4">
        <MembershipThumb />
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <h3 className="text-[22px] font-medium leading-7 text-[#1f1f1f] sm:text-[28px] sm:leading-8">
            {t("bloodTracking.membership.title")}
          </h3>
          <div className="flex flex-wrap items-center gap-1 text-base text-[#777679]">
            <span>{t("bloodTracking.membership.doctorReview")}</span>
            <span className="text-xs">·</span>
            <span>{t("bloodTracking.membership.cancelAnytime")}</span>
          </div>
          <div className="text-[22px] leading-[130%] text-[#2d2d2d]">
            <span className="text-[14px] font-medium tracking-[0.1px]">
              {t("bloodTracking.membership.fromChf")}
            </span>{" "}
            <span className="font-bold">{cheapestAnnual ?? 299}</span>{" "}
            <span className="text-[14px]">
              {t("bloodTracking.membership.annualSuffix")}
            </span>
          </div>
        </div>
      </div>

      {/* Description */}
      <div className="px-4">
        <p className="text-base leading-[1.4] text-black">
          {t("bloodTracking.membership.modalDescription")}
        </p>
      </div>

      {/* Who it's for + Key benefits — icon tiles, same style as the
          Advanced blood-test modal's info tiles. */}
      <div className="flex flex-col gap-2 sm:flex-row">
        <div className="flex min-w-0 flex-1 items-start gap-4 rounded-2xl bg-[#f6f6f6] p-4">
          <PersonHeartIcon className="h-6 w-6 flex-shrink-0 text-[#2d2d2d]" />
          <div className="flex min-w-0 flex-col gap-1 py-1">
            <p className="text-base font-medium leading-[1.2] text-black">
              {t("bloodTracking.membership.whoItsFor")}
            </p>
            <p className="text-sm leading-[1.4] text-black">
              {t("bloodTracking.membership.whoItsForBody")}
            </p>
          </div>
        </div>
        <div className="flex min-w-0 flex-1 items-start gap-4 rounded-2xl bg-[#f6f6f6] p-4">
          <span className="h-6 w-6 flex-shrink-0 text-[#2d2d2d]">
            <CareIcon />
          </span>
          <div className="flex min-w-0 flex-col gap-1 py-1">
            <p className="text-base font-medium leading-[1.2] text-black">
              {t("bloodTracking.membership.keyBenefits")}
            </p>
            <ul className="flex list-disc flex-col gap-0.5 pl-4 text-sm leading-[1.4] text-black">
              <li>{t("bloodTracking.membership.benefit1")}</li>
              <li>{t("bloodTracking.membership.benefit2")}</li>
              <li>{t("bloodTracking.membership.benefit3")}</li>
            </ul>
          </div>
        </div>
      </div>

      {/* What you will get — reuses the comparison card's benefit rows
          (checks, discount glyphs, and the −CHF price chips). */}
      <div className="flex flex-col gap-1">
        <SectionHeading>
          {t("bloodTracking.membership.whatYouGet")}
        </SectionHeading>
        <div className="px-4">
          <MembershipBenefitList language={language} />
        </div>
      </div>

      {/* Membership & pricing (the "information" block) */}
      <div className="flex flex-col gap-1">
        <SectionHeading>
          {t("bloodTracking.membership.pricingHeading")}
        </SectionHeading>
        <div className="rounded-2xl bg-[#f6f6f6] p-4">
          <div className="flex flex-col gap-3">
            {tiers.map((tier) => (
              <div
                key={tier.name}
                className="flex items-baseline justify-between gap-3"
              >
                <div className="flex min-w-0 flex-col">
                  <span className="text-base font-medium text-[#2d2d2d]">
                    {tier.name}
                  </span>
                  <span className="text-sm text-[#777679]">{tier.tests}</span>
                </div>
                <span className="flex-shrink-0 text-base font-medium text-[#2d2d2d]">
                  {tier.price}
                </span>
              </div>
            ))}
            <div className="h-px bg-[#e0e0e0]" />
            <p className="text-sm font-medium text-[#408c49]">
              {t("bloodTracking.membership.savings")}
            </p>
            <p className="text-sm leading-[1.4] text-[#777679]">
              {t("bloodTracking.membership.billedAnnually", {
                annual: String(cheapestAnnual ?? 299),
              })}
            </p>
          </div>
        </div>
      </div>

      {/* Information — reused as-is from the Advanced blood-test popup. */}
      {infoProduct && (
        <ProductInfoSection productId={infoProduct.id} language={language} />
      )}
    </div>
  );

  const buttons = (
    <div className="flex flex-shrink-0 gap-3 px-4 py-4 sm:gap-4 sm:p-6">
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        className="flex h-[40px] flex-1 items-center justify-center rounded-full bg-[#f6f6f6] px-4 text-sm font-medium normal-case text-[#48214a] transition-colors hover:bg-[#ececec] sm:h-[44px] sm:px-8 sm:text-base"
      >
        {t("bloodTracking.modal.close")}
      </button>
      {onStart && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onStart();
          }}
          className="flex h-[40px] flex-1 items-center justify-center gap-2 rounded-full bg-[#48214a] px-4 text-sm font-medium normal-case text-white transition-colors hover:bg-[#371938] sm:h-[44px] sm:px-8 sm:text-base"
        >
          <Check className="h-4 w-4" />
          <span>{t("bloodTracking.membership.cta")}</span>
        </button>
      )}
    </div>
  );

  if (isMobile) {
    return (
      <IOSBottomSheet isOpen={isOpen} onClose={onClose}>
        <div ref={containerRef} className="relative flex h-full flex-col">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            className="absolute right-4 top-4 z-10 rounded-full p-1 text-gray-400 hover:text-gray-600"
            aria-label={t("bloodTracking.modal.close")}
          >
            <X className="h-5 w-5" />
          </button>
          <div className="flex-1 overflow-y-auto pb-10 pt-16">{content}</div>
          <div className="flex-shrink-0 bg-white">{buttons}</div>
        </div>
      </IOSBottomSheet>
    );
  }

  if (!isOpen) return null;

  return (
    <DialogPrimitive.Root open={isOpen} onOpenChange={onClose}>
      <DialogPrimitive.Portal forceMount>
        <div
          className="fixed inset-0 z-50 overflow-y-auto overscroll-none bg-black/80"
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
        >
          <div className="flex min-h-full justify-center px-4 py-8">
            <div
              className="relative h-fit w-full max-w-[800px]"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onClose();
                }}
                className="absolute -right-12 -top-2 z-10 hidden h-8 w-8 items-center justify-center rounded-full bg-white/20 text-white transition-colors hover:bg-white/40 sm:flex"
                aria-label={t("bloodTracking.modal.close")}
              >
                <X className="h-5 w-5" />
              </button>
              <div
                role="dialog"
                aria-modal="true"
                aria-label={t("bloodTracking.membership.modalAria")}
                ref={containerRef}
                className="w-full rounded-3xl bg-background shadow-lg"
              >
                <div className="pb-4 pt-10">{content}</div>
                <div className="sticky bottom-0 z-10">
                  <div className="pointer-events-none h-8 bg-gradient-to-t from-background to-transparent" />
                  <div className="rounded-b-3xl bg-background">{buttons}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};
